import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { publicApi } from '../services/api'
import { formatCurrency } from '../config'
import type { SponsorContact, SponsorshipFormData, SponsorshipConfig, ReferralSource } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ReferralSourceField from '../components/common/ReferralSourceField'
import { formatPhoneNumber, lowercaseEmail, isValidEmail, isValidPhone } from '../utils/formatters'

const focusField = (id: string) => {
  const el = document.getElementById(id)
  if (el) {
    el.focus()
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// Pricing constants (in cents)
const MIN_HOLE_SPONSORSHIP_CENTS = 50000 // $500 minimum
const SILENT_SPONSORSHIP_PRICE_CENTS = 50000 // $500 fixed
const GOLF_FOURSOME_PRICE_CENTS = 60000 // $600
const PROGRAM_PRICING = {
  none: 0,
  coupon: 5000,   // $50
  half: 7500,     // $75
  full: 15000,    // $150
}

const PROGRAM_LABELS = {
  none: 'No Add On',
  coupon: 'Branded Coupon Option Inside Event Program - $50',
  half: 'Branded 1/2 Page in Event Program - $75',
  full: 'Full Branded Page in Event Program - $150',
}

const BRANDED_PAGE_LABELS = {
  none: 'None',
  coupon: 'Branded Coupon Option Inside Event Program - $50',
  half: 'Branded 1/2 Page in Event Program - $75',
  full: 'Full Branded Page in Event Program - $150',
}

const emptyContact = (): SponsorContact => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  businessName: '',
  displayName: '',
})

export default function SponsorForm() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [config, setConfig] = useState<SponsorshipConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [sponsor, setSponsor] = useState<SponsorContact>(emptyContact())
  const [holeAmountError, setHoleAmountError] = useState<string | null>(null)
  const holeAmountRef = useRef<HTMLInputElement>(null)

  // Hole sponsorship
  const [holeSelected, setHoleSelected] = useState(false)
  const [holeAmount, setHoleAmount] = useState('') // User-specified sponsorship amount
  const [holePrizeDescription, setHolePrizeDescription] = useState('')
  const [holePrizeAmount, setHolePrizeAmount] = useState('')
  const [tentRequested, setTentRequested] = useState(false)
  const [tableRunnerAccepted, setTableRunnerAccepted] = useState(false)

  // Silent sponsorship
  const [silentSponsorshipSelected, setSilentSponsorshipSelected] = useState(false)

  // Silent auction
  const [silentAuctionSelected, setSilentAuctionSelected] = useState(false)
  const [silentAuctionPrizeDescription, setSilentAuctionPrizeDescription] = useState('')
  const [silentAuctionPrizeAmount, setSilentAuctionPrizeAmount] = useState('')
  const [silentAuctionProgramAddOn, setSilentAuctionProgramAddOn] = useState<'none' | 'coupon' | 'half' | 'full'>('none')
  // Logo upload (shared across all sponsorship options)
  const [logoKey, setLogoKey] = useState<string | null>(null)
  const [logoFileName, setLogoFileName] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [silentAuctionPrizePickupInfo, setSilentAuctionPrizePickupInfo] = useState('')

  // Standalone program option
  const [brandedPageOption, setBrandedPageOption] = useState<'none' | 'coupon' | 'half' | 'full'>('none')

  // Golf foursome
  const [golfFoursomeSelected, setGolfFoursomeSelected] = useState(false)
  const [foursomeGolfers, setFoursomeGolfers] = useState<Array<{ name: string; email: string; phone: string }>>([
    { name: '', email: '', phone: '' },
    { name: '', email: '', phone: '' },
    { name: '', email: '', phone: '' },
    { name: '', email: '', phone: '' },
  ])

  // Coupon code
  const [couponCode, setCouponCode] = useState('')
  const [couponValidating, setCouponValidating] = useState(false)
  const [couponApplied, setCouponApplied] = useState<{
    code: string
    discountType: 'fixed' | 'percentage'
    discountValue: number
    discountCents: number
  } | null>(null)
  const [couponError, setCouponError] = useState('')

  // Referral source
  const [referralSource, setReferralSource] = useState<ReferralSource | ''>('')
  const [referralSourceOther, setReferralSourceOther] = useState('')

  // Load event config
  useEffect(() => {
    async function fetchConfig() {
      if (!slug) return
      try {
        const data = await publicApi.getSponsorshipConfig(slug)
        setConfig(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sponsorship config')
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [slug])

  // Reset options when silent sponsorship is selected (exclusive with hole and program options)
  useEffect(() => {
    if (silentSponsorshipSelected) {
      setHoleSelected(false)
      setSilentAuctionProgramAddOn('none')
      setBrandedPageOption('none')
    }
  }, [silentSponsorshipSelected])

  // Reset program add-on values when hole sponsorship is selected
  // (hole sponsorship already includes a full page in the event program)
  useEffect(() => {
    if (holeSelected) {
      setSilentSponsorshipSelected(false)
      setSilentAuctionProgramAddOn('none')
      setBrandedPageOption('none')
    }
  }, [holeSelected])

  // Clear coupon when foursome is deselected
  useEffect(() => {
    if (!golfFoursomeSelected) {
      setCouponCode('')
      setCouponApplied(null)
      setCouponError('')
    }
  }, [golfFoursomeSelected])

  // Validate coupon code
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return

    setCouponValidating(true)
    setCouponError('')

    try {
      const result = await publicApi.validateCoupon(
        couponCode,
        'sponsorship_foursome',
        GOLF_FOURSOME_PRICE_CENTS
      )

      if (result.valid && result.coupon) {
        setCouponApplied({
          code: result.coupon.code,
          discountType: result.coupon.discountType,
          discountValue: result.coupon.discountValue,
          discountCents: result.discountCents || 0,
        })
        setCouponCode('')
      } else {
        setCouponError(result.error || 'Invalid coupon code')
        setCouponApplied(null)
      }
    } catch {
      setCouponError('Failed to validate coupon')
      setCouponApplied(null)
    } finally {
      setCouponValidating(false)
    }
  }

  const handleRemoveCoupon = () => {
    setCouponApplied(null)
    setCouponCode('')
    setCouponError('')
  }

  // Calculate totals
  const parseCurrencyToCents = (value: string): number => {
    const num = parseFloat(value.replace(/[^0-9.]/g, ''))
    return isNaN(num) ? 0 : Math.round(num * 100)
  }

  // Determine the effective program option
  const effectiveProgramOption = silentAuctionProgramAddOn !== 'none' ? silentAuctionProgramAddOn : brandedPageOption
  const programOptionSource = silentAuctionProgramAddOn !== 'none' ? 'silentAuctionAddOn' : (brandedPageOption !== 'none' ? 'brandedPageSection' : 'none')

  // Calculate payable total
  let amountDueCents = 0
  const holeAmountCents = parseCurrencyToCents(holeAmount)
  if (holeSelected) {
    amountDueCents += holeAmountCents
  }
  // Silent sponsorship: fixed $500
  if (silentSponsorshipSelected) {
    amountDueCents += SILENT_SPONSORSHIP_PRICE_CENTS
  }
  // Skip program pricing when hole or silent sponsorship is selected (full page already included)
  if (!holeSelected && !silentSponsorshipSelected) {
    amountDueCents += PROGRAM_PRICING[effectiveProgramOption]
  }
  // Golf foursome (with coupon discount if applied)
  let foursomeDiscountCents = 0
  if (golfFoursomeSelected) {
    amountDueCents += GOLF_FOURSOME_PRICE_CENTS
    if (couponApplied) {
      foursomeDiscountCents = couponApplied.discountCents
      amountDueCents -= foursomeDiscountCents
    }
  }

  // Calculate in-kind total
  let inKindTotalCents = 0
  if (holeSelected) {
    inKindTotalCents += parseCurrencyToCents(holePrizeAmount)
  }
  if (silentAuctionSelected) {
    inKindTotalCents += parseCurrencyToCents(silentAuctionPrizeAmount)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setHoleAmountError(null)

    // Validation
    if (!holeSelected && !silentSponsorshipSelected && !silentAuctionSelected && effectiveProgramOption === 'none' && !golfFoursomeSelected) {
      setError('Please select at least one sponsorship option')
      return
    }

    if (holeSelected) {
      if (holeAmountCents < MIN_HOLE_SPONSORSHIP_CENTS) {
        setHoleAmountError('Sponsorship amount must be at least $500')
        holeAmountRef.current?.focus()
        holeAmountRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }
      if (!holePrizeDescription.trim()) {
        setError('Prize description is required for hole sponsorship')
        focusField('hole-prize-description')
        return
      }
      if (parseCurrencyToCents(holePrizeAmount) <= 0) {
        setError('Prize estimated amount must be greater than $0')
        focusField('hole-prize-amount')
        return
      }
    }

    if (silentAuctionSelected) {
      if (!silentAuctionPrizeDescription.trim()) {
        setError('Prize description is required for silent auction')
        focusField('auction-prize-description')
        return
      }
      if (parseCurrencyToCents(silentAuctionPrizeAmount) <= 0) {
        setError('Silent auction prize amount must be greater than $0')
        focusField('auction-prize-amount')
        return
      }
    }

    // Validate sponsor contact email and phone
    if (!isValidEmail(sponsor.email)) {
      setError('Please enter a valid email address')
      focusField('sponsor-email')
      return
    }
    if (!isValidPhone(sponsor.phone)) {
      setError('Please enter a valid 10-digit phone number')
      focusField('sponsor-phone')
      return
    }

    if (golfFoursomeSelected) {
      for (let i = 0; i < foursomeGolfers.length; i++) {
        const g = foursomeGolfers[i]
        if (g.name.trim() && !g.email.trim()) {
          setError(`Golfer ${i + 1} email is required when a name is provided`)
          focusField(`foursome-${i}-email`)
          return
        }
        if (!isValidEmail(g.email)) {
          setError(`Golfer ${i + 1} has an invalid email address`)
          focusField(`foursome-${i}-email`)
          return
        }
        if (!isValidPhone(g.phone)) {
          setError(`Golfer ${i + 1} has an invalid phone number (10 digits required)`)
          focusField(`foursome-${i}-phone`)
          return
        }
      }
    }

    // Validate referral source
    if (!referralSource) {
      setError('Please select how you heard about us')
      return
    }
    if (referralSource === 'Other' && !referralSourceOther.trim()) {
      setError('Please specify how you heard about us')
      return
    }

    setSubmitting(true)

    try {
      const formData: SponsorshipFormData = {
        eventSlug: slug!,
        sponsor,
        holeSelected,
        holeAmountCents: holeSelected ? holeAmountCents : undefined,
        holePrizeDescription: holeSelected ? holePrizeDescription : undefined,
        holePrizeEstimatedAmountCents: holeSelected ? parseCurrencyToCents(holePrizeAmount) : undefined,
        holeTentRequested: holeSelected ? tentRequested : undefined,
        silentSponsorshipSelected,
        silentAuctionSelected,
        silentAuctionPrizeDescription: silentAuctionSelected ? silentAuctionPrizeDescription : undefined,
        silentAuctionPrizeAmountCents: silentAuctionSelected ? parseCurrencyToCents(silentAuctionPrizeAmount) : undefined,
        silentAuctionProgramAddOn: silentAuctionSelected ? (silentAuctionProgramAddOn !== 'none') : undefined,
        silentAuctionLogoUrl: logoKey || undefined,
        silentAuctionPrizePickupInfo: silentAuctionSelected ? silentAuctionPrizePickupInfo || undefined : undefined,
        programOption: effectiveProgramOption,
        programOptionSource: programOptionSource as 'none' | 'silentAuctionAddOn' | 'brandedPageSection',
        tableRunnerAccepted: holeSelected ? tableRunnerAccepted : undefined,
        golfFoursomeSelected: golfFoursomeSelected || undefined,
        golfFoursomeGolfers: golfFoursomeSelected ? foursomeGolfers.filter(g => g.name || g.email || g.phone) : undefined,
        couponCode: couponApplied?.code || undefined,
        couponDiscountCents: couponApplied ? foursomeDiscountCents : undefined,
        referralSource: referralSource as ReferralSource,
        referralSourceOther: referralSource === 'Other' ? referralSourceOther.trim() : undefined,
      }

      const response = await publicApi.submitSponsorship(formData)

      if (response.amountDueCents > 0) {
        // Create Stripe checkout and redirect
        const checkoutResponse = await publicApi.createSponsorshipCheckout(response.submissionId)
        window.location.href = checkoutResponse.url
      } else {
        // Zero-dollar flow - go directly to success page
        navigate(`/s/${slug}/success?submissionId=${response.submissionId}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit sponsorship')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !config) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <a href="/" className="btn btn-primary">
          Back to Events
        </a>
      </div>
    )
  }

  if (!config) {
    return null
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="card mb-8 overflow-hidden">
        <div className="-mx-6 -mt-6 mb-6 overflow-hidden">
          <img
            src="/images/angels-on-the-green.png"
            alt="Angels on the Green Golf Tournament"
            className="w-full h-48 md:h-64 object-cover object-center"
          />
        </div>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <img
            src="/images/logo.png"
            alt="School Lunch Angels"
            className="h-32 md:h-40 w-auto flex-shrink-0"
          />
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-sla-green mb-2">Angels on the Green</h1>
            <p className="text-xl text-gray-700 mb-2">School Lunch Angels Best Ball Scramble Golf Tournament</p>
            <p className="text-lg font-semibold text-sla-blue mb-4">Sponsorship Form</p>
            <p className="text-gray-600">
              All proceeds benefit School Lunch Angels and help eliminate school lunch debt from students in Harris County, GA.
            </p>
          </div>
        </div>
      </div>

      {/* Event Information */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Event Information</h2>
        <div className="grid md:grid-cols-2 gap-4 text-gray-700">
          <div>
            <p><strong>Date:</strong> April 11, 2026</p>
            <p><strong>Location:</strong> Callaway Resort & Gardens</p>
            <p className="text-gray-600 ml-4">Lake View Golf Course</p>
            <p className="text-gray-600 ml-4">3396 Golf House Drive, Pine Mountain, GA 31822</p>
          </div>
          <div>
            <p><strong>Check In Time:</strong> 11:00 AM</p>
            <p><strong>Shotgun Start:</strong> 12:00 PM</p>
            <p><strong>Awards Ceremony:</strong> Following Tournament</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Sponsor Contact Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Sponsor Contact Information
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input
                type="text"
                className="input"
                value={sponsor.firstName}
                onChange={(e) => setSponsor({ ...sponsor, firstName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input
                type="text"
                className="input"
                value={sponsor.lastName}
                onChange={(e) => setSponsor({ ...sponsor, lastName: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                id="sponsor-email"
                type="email"
                className="input"
                value={sponsor.email}
                onChange={(e) => setSponsor({ ...sponsor, email: e.target.value })}
                onBlur={(e) => setSponsor({ ...sponsor, email: lowercaseEmail(e.target.value) })}
                required
              />
            </div>
            <div>
              <label className="label">Phone *</label>
              <input
                id="sponsor-phone"
                type="tel"
                className="input"
                value={sponsor.phone}
                onChange={(e) => setSponsor({ ...sponsor, phone: formatPhoneNumber(e.target.value) })}
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="label">Business or Organization Name</label>
              <input
                type="text"
                className="input"
                value={sponsor.businessName}
                onChange={(e) => setSponsor({ ...sponsor, businessName: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="label">Sponsor Display Name</label>
              <p className="text-sm text-gray-500 mb-1">
                Name for printed materials (defaults to business name or full name)
              </p>
              <input
                type="text"
                className="input"
                value={sponsor.displayName}
                onChange={(e) => setSponsor({ ...sponsor, displayName: e.target.value })}
                placeholder={sponsor.businessName || `${sponsor.firstName} ${sponsor.lastName}`.trim()}
              />
            </div>
          </div>
        </div>

        {/* Sponsorship Options Header */}
        <div className="bg-sla-green text-white rounded-lg p-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Select Your Sponsorship Options</h2>
          <p className="text-green-100">Choose one or more options below to support School Lunch Angels</p>
        </div>

        {/* Silent Sponsorship Section */}
        <div className={`card border-2 ${silentSponsorshipSelected ? 'border-sla-green ring-2 ring-sla-green bg-green-50' : 'border-gray-200 hover:border-sla-green'}`}>
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="silentSponsorship"
              className="mt-2 h-6 w-6 rounded border-gray-300 text-sla-green focus:ring-sla-green"
              checked={silentSponsorshipSelected}
              onChange={(e) => setSilentSponsorshipSelected(e.target.checked)}
              disabled={holeSelected}
            />
            <div className="flex-1">
              <label htmlFor="silentSponsorship" className="text-xl md:text-2xl font-bold text-sla-green cursor-pointer block">
                Silent Sponsorship - {formatCurrency(SILENT_SPONSORSHIP_PRICE_CENTS)}
              </label>
              <p className="text-sm text-gray-500 mt-1">Support the event without being at a hole</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><span className="text-sla-green">✓</span> Branded Flag</span>
                <span className="flex items-center gap-1"><span className="text-sla-green">✓</span> Awards Recognition</span>
                <span className="flex items-center gap-1"><span className="text-sla-green">✓</span> Full Program Page</span>
                <span className="flex items-center gap-1"><span className="text-sla-green">✓</span> Social Media Shout Out</span>
                <span className="flex items-center gap-1"><span className="text-sla-green">✓</span> Tax Deductible Receipt</span>
              </div>
            </div>
          </div>

          {silentSponsorshipSelected && (
            <div className="mt-6 ml-9 space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 mb-3">Included Benefits:</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Branded Flag at Hole</li>
                  <li>Recognition at Awards Ceremony</li>
                  <li>Full Branded Page in Event Program</li>
                  <li>Social Media Thank You Shout Out</li>
                  <li>Tax Deductible Receipt</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4 italic">
                  Hole assignment will be made by the event organizers
                </p>
              </div>

            </div>
          )}
        </div>

        {/* Hole Sponsorship Section */}
        <div className={`card border-2 ${holeSelected ? 'border-sla-blue ring-2 ring-sla-blue bg-blue-50' : 'border-gray-200 hover:border-sla-blue'}`}>
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="holeSponsorship"
              className="mt-2 h-6 w-6 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
              checked={holeSelected}
              onChange={(e) => setHoleSelected(e.target.checked)}
              disabled={silentSponsorshipSelected}
            />
            <div className="flex-1">
              <label htmlFor="holeSponsorship" className="text-xl md:text-2xl font-bold text-sla-blue cursor-pointer block">
                Hole Sponsorship + Giveaway Prize
              </label>
              <p className="text-sm text-gray-600 mt-1">Be present at your own hole with a table setup and prize giveaway</p>
              <p className="text-sm text-gray-500 mt-1">Due March 1, 2026</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Table & Chairs at Hole</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Branded Flag</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Awards Recognition</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Full Program Page</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Social Media Shout Out</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Tax Deductible Receipt</span>
              </div>
            </div>
          </div>

          {holeSelected && (
            <div className="mt-6 ml-9 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-700">
                  You will be stationed at the tee box where you can engage with golfers as they come through
                </p>
              </div>

              <div>
                <label className="label">Sponsorship Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    ref={holeAmountRef}
                    type="text"
                    className={`input pl-7 ${holeAmountError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    value={holeAmount}
                    onChange={(e) => {
                      setHoleAmount(e.target.value)
                      setHoleAmountError(null)
                    }}
                    placeholder="0.00"
                    required={holeSelected}
                  />
                </div>
                {holeAmountError ? (
                  <p className="text-sm text-red-600 mt-1">{holeAmountError}</p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">This amount will be added to your payment total</p>
                )}
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Included Benefits:</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Hole challenge will be at your table and winner will receive your prize at the awards ceremony</li>
                  <li>Personal 6' Table, 2 Chairs, Table Cloth</li>
                  <li>Branded Flag at Hole</li>
                  <li>Recognition at Awards Ceremony</li>
                  <li>Full Branded Page in Event Program</li>
                  <li>Social Media Thank You Shout Out</li>
                  <li>Tax Deductible Receipt</li>
                </ul>
              </div>

              <div>
                <label className="label">Prize Description *</label>
                <textarea
                  id="hole-prize-description"
                  className="input"
                  rows={3}
                  value={holePrizeDescription}
                  onChange={(e) => setHolePrizeDescription(e.target.value)}
                  placeholder="Describe the giveaway prize you will provide"
                  required={holeSelected}
                />
              </div>

              <div>
                <label className="label">Estimated Prize Value *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    id="hole-prize-amount"
                    type="text"
                    className="input pl-7"
                    value={holePrizeAmount}
                    onChange={(e) => setHolePrizeAmount(e.target.value)}
                    placeholder="0.00"
                    required={holeSelected}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">This is for in-kind tracking only and does not affect your payment total</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-700">
                  Please bring your company's branded tent or table runner for your hole sponsorship
                </p>
              </div>

              <div>
                <p className="font-medium text-gray-700 mb-3">Requesting to be provided, Please select all that apply:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
                      checked={tentRequested}
                      onChange={(e) => setTentRequested(e.target.checked)}
                    />
                    <span>TENT</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5 rounded border-gray-300 text-sla-blue focus:ring-sla-blue"
                      checked={tableRunnerAccepted}
                      onChange={(e) => setTableRunnerAccepted(e.target.checked)}
                    />
                    <span>BRANDED TABLE RUNNER</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Silent Auction Section */}
        <div className={`card border-2 ${silentAuctionSelected ? 'border-purple-500 ring-2 ring-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-500'}`}>
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="silentAuction"
              className="mt-2 h-6 w-6 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              checked={silentAuctionSelected}
              onChange={(e) => setSilentAuctionSelected(e.target.checked)}
            />
            <div className="flex-1">
              <label htmlFor="silentAuction" className="text-xl md:text-2xl font-bold text-purple-600 cursor-pointer block">
                Silent Auction Contribution
              </label>
              <p className="text-sm text-gray-600 mt-1">Donate an item or experience to be auctioned at the event</p>
              <p className="text-sm text-gray-500 mt-1">Due March 1, 2026</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> In-Kind Contribution</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Program Recognition</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Tax Deductible Receipt</span>
              </div>
            </div>
          </div>

          {silentAuctionSelected && (
            <div className="mt-6 ml-9 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-3">Included Benefits:</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Logo and Recognition on Auction Contribution Page in Event Program</li>
                  <li>Tax Deductible Receipt</li>
                </ul>
              </div>

              <div>
                <label className="label">Prize Description *</label>
                <textarea
                  id="auction-prize-description"
                  className="input"
                  rows={3}
                  value={silentAuctionPrizeDescription}
                  onChange={(e) => setSilentAuctionPrizeDescription(e.target.value)}
                  placeholder="Describe your silent auction contribution"
                  required={silentAuctionSelected}
                />
              </div>

              <div>
                <label className="label">Amount of Prize *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    id="auction-prize-amount"
                    type="text"
                    className="input pl-7"
                    value={silentAuctionPrizeAmount}
                    onChange={(e) => setSilentAuctionPrizeAmount(e.target.value)}
                    placeholder="0.00"
                    required={silentAuctionSelected}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">This is for in-kind tracking only and does not affect your payment total</p>
              </div>

              {/* Prize Pickup Info */}
              <div>
                <label className="label">Prize Pickup Information (Optional)</label>
                <textarea
                  className="input"
                  rows={2}
                  value={silentAuctionPrizePickupInfo}
                  onChange={(e) => setSilentAuctionPrizePickupInfo(e.target.value)}
                  placeholder="Address or instructions for prize pickup"
                />
                <p className="text-sm text-gray-500 mt-1">
                  If left blank, we'll contact you about pickup arrangements
                </p>
                <p className="text-sm text-orange-600 mt-1 font-medium">
                  March 31st is the deadline for prize pickups.
                </p>
              </div>

              {/* Hide add-on options when hole or silent sponsorship is selected (full page already included) */}
              {!holeSelected && !silentSponsorshipSelected && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Silent Auction Additional Add On's:</h3>
                  <div className="space-y-2">
                    {(['none', 'coupon', 'half', 'full'] as const).map((option) => (
                      <label key={option} className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="silentAuctionAddOn"
                          className="h-5 w-5 border-gray-300 text-sla-blue focus:ring-sla-blue"
                          checked={silentAuctionProgramAddOn === option}
                          onChange={() => {
                            setSilentAuctionProgramAddOn(option)
                            // Clear branded page option if selecting here
                            if (option !== 'none') {
                              setBrandedPageOption('none')
                            }
                          }}
                        />
                        <span>{PROGRAM_LABELS[option]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Branded Page in Program Section */}
        {/* Hide if hole or silent sponsorship selected (full page already included) or if add-on selected in Silent Auction */}
        {!holeSelected && !silentSponsorshipSelected && silentAuctionProgramAddOn === 'none' && (
          <div className={`card border-2 ${brandedPageOption !== 'none' ? 'border-teal-500 ring-2 ring-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-500'}`}>
            <h2 className="text-xl md:text-2xl font-bold text-teal-600 mb-2">
              Branded Page in Event Program
            </h2>
            <p className="text-sm text-gray-600 mb-1">Advertise your business in the printed event program</p>
            <p className="text-sm text-gray-500 mb-4">Due March 1, 2026</p>

            <p className="text-gray-600 mb-4">Includes Tax Deductible Receipt</p>

            <div className="space-y-2">
              {(['none', 'coupon', 'half', 'full'] as const).map((option) => (
                <label key={option} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="brandedPageOption"
                    className="h-5 w-5 border-gray-300 text-sla-blue focus:ring-sla-blue"
                    checked={brandedPageOption === option}
                    onChange={() => setBrandedPageOption(option)}
                  />
                  <span>{BRANDED_PAGE_LABELS[option]}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Golf Foursome Registration */}
        <div className={`card border-2 ${golfFoursomeSelected ? 'border-orange-500 ring-2 ring-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-500'}`}>
          <div className="flex items-start gap-4">
            <input
              type="checkbox"
              id="golfFoursome"
              className="mt-2 h-6 w-6 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              checked={golfFoursomeSelected}
              onChange={(e) => setGolfFoursomeSelected(e.target.checked)}
            />
            <div className="flex-1">
              <label htmlFor="golfFoursome" className="text-xl md:text-2xl font-bold text-orange-600 cursor-pointer block">
                Golf Foursome Registration - {formatCurrency(GOLF_FOURSOME_PRICE_CENTS)}
              </label>
              <p className="text-sm text-gray-500 mt-1">Register a foursome of 4 golfers for the tournament</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> 4 Golfer Registrations</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Tournament Entry</span>
                <span className="flex items-center gap-1"><span className="text-sla-blue">✓</span> Tax Deductible Receipt</span>
              </div>
            </div>
          </div>

          {golfFoursomeSelected && (
            <div className="mt-6 ml-9 space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-gray-700">
                  Golfer details are optional at this time. You can provide them now or they can be added later by an administrator.
                </p>
              </div>

              {foursomeGolfers.map((golfer, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-700 mb-3">Golfer {index + 1}</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Name</label>
                      <input
                        id={`foursome-${index}-name`}
                        type="text"
                        className="input"
                        value={golfer.name}
                        onChange={(e) => {
                          const updated = [...foursomeGolfers]
                          updated[index] = { ...updated[index], name: e.target.value }
                          setFoursomeGolfers(updated)
                        }}
                        placeholder="Golfer name"
                      />
                    </div>
                    <div>
                      <label className="label">Email</label>
                      <input
                        id={`foursome-${index}-email`}
                        type="email"
                        className="input"
                        value={golfer.email}
                        onChange={(e) => {
                          const updated = [...foursomeGolfers]
                          updated[index] = { ...updated[index], email: e.target.value }
                          setFoursomeGolfers(updated)
                        }}
                        onBlur={(e) => {
                          const updated = [...foursomeGolfers]
                          updated[index] = { ...updated[index], email: lowercaseEmail(e.target.value) }
                          setFoursomeGolfers(updated)
                        }}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="label">Phone</label>
                      <input
                        id={`foursome-${index}-phone`}
                        type="tel"
                        className="input"
                        value={golfer.phone}
                        onChange={(e) => {
                          const updated = [...foursomeGolfers]
                          updated[index] = { ...updated[index], phone: formatPhoneNumber(e.target.value) }
                          setFoursomeGolfers(updated)
                        }}
                        placeholder="(555) 555-5555"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Coupon Code Input */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h4 className="font-medium text-gray-700 mb-3">Have a coupon code?</h4>
                {couponApplied ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                      <span className="font-mono font-bold text-green-700">{couponApplied.code}</span>
                      <span className="text-green-600 ml-2">
                        -{couponApplied.discountType === 'fixed'
                          ? formatCurrency(couponApplied.discountCents)
                          : `${couponApplied.discountValue}%`} applied
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1 uppercase"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase())
                        setCouponError('')
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleValidateCoupon()
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleValidateCoupon}
                      disabled={couponValidating || !couponCode.trim()}
                      className="btn btn-secondary"
                    >
                      {couponValidating ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                )}
                {couponError && (
                  <p className="text-red-600 text-sm mt-2">{couponError}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Logo Upload - shown if any sponsorship option is selected */}
        {(holeSelected || silentSponsorshipSelected || silentAuctionSelected || effectiveProgramOption !== 'none') && (
          <div className="card">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Company/Organization Logo</h2>
            <p className="text-gray-600 mb-4">
              Upload your logo to be included in the event program for your Hole Sponsorship, Silent Auction, or Branded Page option.
            </p>
            {logoKey ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-700">Logo uploaded: {logoFileName}</span>
                <button
                  type="button"
                  className="ml-auto text-sm text-red-600 hover:text-red-800"
                  onClick={() => {
                    setLogoKey(null)
                    setLogoFileName(null)
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-2">
                  PNG, JPG, SVG, or PDF format (max 20MB)
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  For best print quality, please provide your logo in CMYK color mode if available.
                </p>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf,.svg,image/png,image/jpeg,application/pdf,image/svg+xml"
                  className="input"
                  disabled={logoUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file) return

                    // Validate file size (max 20MB)
                    if (file.size > 20 * 1024 * 1024) {
                      setError('File size must be less than 20MB')
                      return
                    }

                    // Validate file type
                    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/svg+xml']
                    if (!allowedTypes.includes(file.type)) {
                      setError('File must be PNG, JPG, SVG, or PDF')
                      return
                    }

                    setLogoUploading(true)
                    setError(null)

                    try {
                      // Get presigned URL
                      const { uploadUrl, logoKey: newLogoKey } = await publicApi.getSponsorLogoUploadUrl(
                        file.name,
                        file.type
                      )

                      // Upload directly to S3
                      const uploadResponse = await fetch(uploadUrl, {
                        method: 'PUT',
                        body: file,
                        headers: {
                          'Content-Type': file.type,
                        },
                      })

                      if (!uploadResponse.ok) {
                        throw new Error('Failed to upload logo')
                      }

                      setLogoKey(newLogoKey)
                      setLogoFileName(file.name)
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to upload logo')
                    } finally {
                      setLogoUploading(false)
                      // Reset file input
                      e.target.value = ''
                    }
                  }}
                />
                {logoUploading && (
                  <div className="flex items-center gap-2 mt-2 text-gray-600">
                    <LoadingSpinner size="sm" />
                    <span>Uploading...</span>
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  If you are unable to upload your logo, please email it to{' '}
                  <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
                    secretary@schoollunchangels.org
                  </a>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Referral Source */}
        <ReferralSourceField
          value={referralSource}
          otherValue={referralSourceOther}
          onChange={setReferralSource}
          onOtherChange={setReferralSourceOther}
          error={!!error && !referralSource}
        />

        {/* Order Summary */}
        <div className="card bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Order Summary</h2>

          <div className="space-y-2 mb-4">
            {holeSelected && holeAmountCents > 0 && (
              <div className="flex justify-between">
                <span>Hole Sponsorship</span>
                <span>{formatCurrency(holeAmountCents)}</span>
              </div>
            )}
            {silentSponsorshipSelected && (
              <div className="flex justify-between">
                <span>Silent Sponsorship</span>
                <span>{formatCurrency(SILENT_SPONSORSHIP_PRICE_CENTS)}</span>
              </div>
            )}
            {effectiveProgramOption !== 'none' && !holeSelected && !silentSponsorshipSelected && (
              <div className="flex justify-between">
                <span>
                  {effectiveProgramOption === 'coupon' && 'Branded Coupon in Event Program'}
                  {effectiveProgramOption === 'half' && 'Branded 1/2 Page in Event Program'}
                  {effectiveProgramOption === 'full' && 'Full Branded Page in Event Program'}
                </span>
                <span>{formatCurrency(PROGRAM_PRICING[effectiveProgramOption])}</span>
              </div>
            )}
            {golfFoursomeSelected && (
              <div className="flex justify-between">
                <span>Golf Foursome Registration (4 golfers)</span>
                <span>{formatCurrency(GOLF_FOURSOME_PRICE_CENTS)}</span>
              </div>
            )}
            {couponApplied && foursomeDiscountCents > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Coupon ({couponApplied.code})</span>
                <span>-{formatCurrency(foursomeDiscountCents)}</span>
              </div>
            )}
          </div>

          {inKindTotalCents > 0 && (
            <div className="border-t pt-4 mb-4">
              <div className="flex justify-between text-gray-600">
                <span>In-Kind Contributions:</span>
                <span>{formatCurrency(inKindTotalCents)}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                (Tax deductible - not included in payment total)
              </p>
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex justify-between text-xl font-bold">
              <span>Total Due Today</span>
              <span className="text-sla-blue">{formatCurrency(amountDueCents)}</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={submitting || (!holeSelected && !silentSponsorshipSelected && !silentAuctionSelected && effectiveProgramOption === 'none' && !golfFoursomeSelected)}
            className="btn btn-primary text-lg px-8 py-3"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                Processing...
              </span>
            ) : amountDueCents > 0 ? (
              `Proceed to Payment - ${formatCurrency(amountDueCents)}`
            ) : (
              'Submit Sponsorship'
            )}
          </button>
          <p className="text-gray-500 text-sm mt-2">
            {amountDueCents > 0
              ? "You'll be redirected to Stripe for secure payment"
              : "Your sponsorship will be submitted immediately"
            }
          </p>
        </div>
      </form>
    </div>
  )
}
